import PropTypes from "prop-types";
import { useState } from "react";
import { toast } from "sonner";

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { formatError } from "helpers/formatError";
import { useRevenueOpportunityMutations } from "hooks/useRevenueOpportunity";

const SWITCH_ID = "revenue-opportunity-lock-state";

/**
 * The Admin-only lock control on the edit page. It applies at once, outside
 * the form's Save, because locking is its own audited action.
 */
const LockStateToggle = ({ revenueOpportunityId, isLocked }) => {
  const { setLocked } = useRevenueOpportunityMutations();
  const [checked, setChecked] = useState(isLocked);

  const handleChange = locked =>
    setLocked.mutate(
      { id: revenueOpportunityId, locked },
      {
        onSuccess: () => {
          setChecked(locked);
          toast.success(
            locked
              ? "Revenue Opportunity locked"
              : "Revenue Opportunity unlocked"
          );
        },
        onError: error =>
          toast.error(formatError(error, "Could not change the lock state")),
      }
    );

  return (
    <Field orientation="horizontal">
      <Switch
        id={SWITCH_ID}
        checked={checked}
        disabled={setLocked.isPending}
        onCheckedChange={handleChange}
      />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <FieldLabel htmlFor={SWITCH_ID}>Lock State</FieldLabel>
        <FieldDescription>
          A locked Revenue Opportunity is read-only for every non-Admin user.
          This change applies immediately.
        </FieldDescription>
      </div>
    </Field>
  );
};

LockStateToggle.propTypes = {
  revenueOpportunityId: PropTypes.number.isRequired,
  isLocked: PropTypes.bool.isRequired,
};

export default LockStateToggle;
